import React, { useState } from 'react';
import { CircleAlert, CircleCheck, CircleX, Eye, EyeOff } from 'lucide-react';

/**
 * Small pieces shared by the Profile → settings screens (the app's
 * screens/settings/*). Styles: screens/settings/settings.scss.
 */

/** Placeholder rows while a settings screen loads. */
export function SettingsLoading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="brd-container brd-container--narrow brd-settings__body" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="brd-skeleton brd-settings__skeleton" />
      ))}
    </div>
  );
}

/** A load that failed, with Try again. */
export function SettingsError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="brd-empty brd-settings__state" role="alert">
      <span className="brd-empty__icon"><CircleAlert size={28} /></span>
      <p className="brd-empty__title">Something went wrong</p>
      <p className="brd-empty__body">{message}</p>
      <button type="button" className="brd-btn brd-btn--secondary" onClick={onRetry}>Try again</button>
    </div>
  );
}

/** On / off with Yes / No — the app's ModernToggleSwitch. */
export function Toggle({ checked, onChange, label, disabled }: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** What it switches, for screen readers (e.g. "New Campaign Alert by Email"). */
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`brd-switch${checked ? ' brd-switch--on' : ''}`}
      onClick={() => onChange(!checked)}
      disabled={disabled}
    >
      <span className="brd-switch__label">{checked ? 'Yes' : 'No'}</span>
      <span className="brd-switch__knob" aria-hidden />
    </button>
  );
}

/** A labelled read-only value; `verified` adds the green ✓ / red ✕. */
export function ReadOnlyField({ label, value, verified }: { label: string; value: string; verified?: boolean }) {
  return (
    <div className="brd-field">
      <span className="brd-field__label">{label}</span>
      <div className="brd-readonly" style={{ marginTop: 4 }}>
        <span className="brd-readonly__text">{value || '—'}</span>
        {verified === true && <CircleCheck size={18} className="brd-readonly__ok" aria-label="Verified" />}
        {verified === false && <CircleX size={18} className="brd-readonly__no" aria-label="Not verified" />}
      </div>
    </div>
  );
}

/** A password / PIN box, masked by default, with its own show-hide eye (as in the app). */
export function SecretInput({ id, value, onChange, placeholder, numeric, invalid, autoFocus, autoComplete, maxLength }: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** PIN: digits only, number keypad on phones. */
  numeric?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  maxLength?: number;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="brd-secret">
      <input
        id={id}
        className={`brd-input${invalid ? ' brd-input--invalid' : ''}`}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(numeric ? e.target.value.replace(/\D/g, '') : e.target.value)}
        placeholder={placeholder}
        inputMode={numeric ? 'numeric' : undefined}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        maxLength={maxLength}
        aria-invalid={invalid || undefined}
      />
      <button
        type="button"
        className="brd-secret__eye"
        onClick={() => setVisible((v) => !v)}
        aria-label={`${visible ? 'Hide' : 'Show'} ${placeholder.toLowerCase()}`}
      >
        {visible ? <Eye size={18} /> : <EyeOff size={18} />}
      </button>
    </div>
  );
}
